import { AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'

const SESSION_CONTEXT_HEADER = 'x-session-context'

interface SessionContextSnapshot {
  user: string
  scope: string
  company: string
  group: string
  network: string
}

class SessionContextService {
  /**
   * Parse the `X-Session-Context` response header (emitted by the API on every
   * authenticated response, e.g.
   * `user=1;scope=COMPANY;company=2;group=;network=`) into its component parts.
   * Returns null when the header is absent (e.g. unauthenticated requests, or a
   * surface where no context could be resolved).
   */
  private parseHeader(
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
  ): SessionContextSnapshot | null {
    const raw = headers[SESSION_CONTEXT_HEADER]

    if (typeof raw !== 'string' || raw.length === 0) {
      return null
    }

    const values: Record<string, string> = {}
    for (const pair of raw.split(';')) {
      const separatorIndex = pair.indexOf('=')
      if (separatorIndex === -1) {
        continue
      }
      values[pair.slice(0, separatorIndex)] = pair.slice(separatorIndex + 1)
    }

    return {
      user: values.user ?? '',
      scope: values.scope ?? '',
      company: values.company ?? '',
      group: values.group ?? '',
      network: values.network ?? '',
    }
  }

  /**
   * The context the frontend was initialised with, read from the session
   * metadata mirrored onto `window.RQ.store`. Returns null before the session
   * has been bootstrapped (nothing to compare against yet).
   */
  private getExpectedContext(): SessionContextSnapshot | null {
    const metadata = window.RQ?.store?.session?.metadata

    if (!metadata) {
      return null
    }

    return {
      user: String(metadata.currentUser.id),
      scope: metadata.currentUserType ?? '',
      company: metadata.currentCompany
        ? String(metadata.currentCompany.id)
        : '',
      group: metadata.currentGroup ? String(metadata.currentGroup.id) : '',
      network: metadata.currentNetwork
        ? String(metadata.currentNetwork.id)
        : '',
    }
  }

  /**
   * True only when both sides hold a concrete (non-empty) value and they
   * differ. An empty `actual` (context was dropped) or an empty `expected` (an
   * additive gain, e.g. no company context then a company is selected) is not
   * treated as a switch - only a change between two concrete, differing values.
   */
  private isConcreteSwitch(expected: string, actual: string): boolean {
    return expected !== '' && actual !== '' && expected !== actual
  }

  /**
   * Detect when the server-side session has been switched to a *different*
   * user/scope/company/group/network than the one the frontend was initialised
   * with - e.g. Keycloak admin impersonation, a global admin switching our context, or
   * a switch made on another surface sharing the session cookie (API docs UI,
   * Office add-in, another tab).
   *
   * Deliberate in-app switches are excluded by the caller: they hit the context
   * endpoint and then reload the page, which re-establishes the baseline.
   *
   * Additive changes are intentionally not flagged (see `isConcreteSwitch`), so
   * gaining a company/scope/group/network where there was none before does not
   * log the user out.
   */
  hasUnexpectedContextSwitch(
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
  ): boolean {
    const actual = this.parseHeader(headers)
    const expected = this.getExpectedContext()

    if (!actual || !expected) {
      return false
    }

    return (
      this.isConcreteSwitch(expected.user, actual.user) ||
      this.isConcreteSwitch(expected.scope, actual.scope) ||
      this.isConcreteSwitch(expected.company, actual.company) ||
      this.isConcreteSwitch(expected.group, actual.group) ||
      this.isConcreteSwitch(expected.network, actual.network)
    )
  }

  /**
   * The context this tab is showing, sent as `X-Session-Context-Hint` so the
   * API can restore it if its stored context expired while the tab was open,
   * instead of answering with no group or company selected.
   */
  getContextHint(): string | null {
    const expected = this.getExpectedContext()

    if (!expected?.scope) {
      return null
    }

    return `scope=${expected.scope};company=${expected.company};group=${expected.group};network=${expected.network}`
  }
}

const sessionContextService = new SessionContextService()

export default sessionContextService
